// @ts-nocheck
import PaymentSummaryItem from '../PaymentSummaryItem/PaymentSummaryItem';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as PropTypes from 'prop-types';
import './PaymentSummary.css';

/**
 * Functional React component for rendering a custom payment summary section.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {string} [props.id] - The ID for the booking totals component.
 * @param {string} [props.items] - The items for the payment summary component.
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the payment summary component.
 */

const PaymentSummary = ({
  id,
  items = [],
  bookingId,
  headerContent,
  headerHelperText,
  footerContent,
  currencyConfig,
  ...rest
}) => {
  const { t } = useTranslation(['trip', 'common', 'errors']);
  const mainPaymentItems = items?.filter((item) => !item?.isThisPayment);
  const thisPaymentItems = items?.filter((item) => !!item?.isThisPayment);
  const amountToCharge =
    thisPaymentItems?.find((item) => item?.key === 'amountToCharge')?.amount ??
    0;

  return (
    <Box
      className='payment-summary'
      data-testid='payment-summary'
      id={id}
      {...rest}
    >
      <div className='payment-summary__header'>
        <span className='h4-semibold '>
          {t('trip:paymentSummaryForBooking', { bookingId: bookingId ?? '' })}
        </span>
        {headerContent}
      </div>
      {headerHelperText && (
        <span className='payment-summary__helper'>{headerHelperText}</span>
      )}
      {mainPaymentItems && (
        <div
          className='payment-summary__main-payment'
          style={{
            marginBottom:
              amountToCharge <= 0 && !footerContent ? '24px' : 'initial'
          }}
        >
          <div className='payment-summary__items'>
            {mainPaymentItems.map((item) => {
              return (
                item?.show && (
                  <div
                    key={item.id}
                    className='payment-summary__item-container'
                  >
                    <PaymentSummaryItem
                      id={`${item.id}`}
                      data={{ ...item, currencyConfig }}
                    />
                  </div>
                )
              );
            })}
          </div>
        </div>
      )}

      {amountToCharge > 0 && thisPaymentItems && (
        <div className='payment-summary__this-payment'>
          <span className='text-sm-bold payment-summary__this-payment-title '>
            {t('trip:thisPayment')}
          </span>
          <div className='payment-summary__items'>
            {thisPaymentItems.map((item) => {
              return (
                item?.show && (
                  <div
                    key={item.id}
                    className='payment-summary__item-container payment-summary__item-this-payment'
                  >
                    <PaymentSummaryItem
                      id={`${item.id}`}
                      data={{ ...item, currencyConfig }}
                    />
                  </div>
                )
              );
            })}
          </div>
          {footerContent && (
            <div className='payment-summary__footer'>{footerContent}</div>
          )}
        </div>
      )}
      {amountToCharge <= 0 && footerContent && (
        <div className='payment-summary__footer'>{footerContent}</div>
      )}
    </Box>
  );
};

PaymentSummary.propTypes = {
  id: PropTypes.string.isRequired,
  items: PropTypes.array,
  bookingId: PropTypes.number,
  headerContent: PropTypes.node,
  headerHelperText: PropTypes.string,
  footerContent: PropTypes.node,
  currencyConfig: PropTypes.object
};

export default PaymentSummary;
